import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, firstValueFrom, Observable, map, of, take, timeout } from 'rxjs';
import { AuthApiService } from './auth-api.service';
import { Router } from '@angular/router';
import { SessionData } from '../helper/auth.helper';
import { LoginResponse } from '../helper/dto/login-response.dto';
import { AuthSessionDto } from '../helper/dto/auth-session.dto';
import { ProfileDto } from '../helper/dto/profile.dto';

const RESTORE_SESSION_TIMEOUT_MS = 10000;

@Injectable({ providedIn: 'root' })
export class AuthService {
    private _sessionData = new BehaviorSubject<SessionData>(new SessionData());
    readonly sessionData = this._sessionData.asObservable();

    constructor(
        private authApiService: AuthApiService,
        private router: Router
    ) {}

    private applySession(claims: AuthSessionDto, stage?: SessionData['stage']): SessionData {
        const session = new SessionData();

        session.tokenType = claims.tokenType;
        session.user._id = claims._id ?? '';
        session.user.name = claims.name ?? '';
        session.user.email = claims.mail ?? '';
        session.user.roles = claims.roles ?? [];
        session.user.active = claims.active;
        session.user.expires = claims.expires;
        session.user.isSystemAdmin = claims.isSystemAdmin;
        session.user.oidcIdentifier = claims.oidcIdentifier ?? '';
        session.user.profile = claims.profile ?? new ProfileDto();
        session.actor = claims.tokenType === 'actortoken' ? claims.actor : undefined;

        if (claims.tokenType === 'twofactortoken') {
            session.isLoggedIn = false;
            session.require2Fa = true;
            session.twofactors = claims.twofactors ?? [];
            session.stage = 'twofa';
        } else {
            session.isLoggedIn = true;
            session.require2Fa = false;
        }

        if (stage) {
            session.stage = stage;
        }

        this._sessionData.next(session);
        return session;
    }

    async restoreSession(): Promise<boolean> {
        const result = await firstValueFrom(this.authApiService.me().pipe(timeout(RESTORE_SESSION_TIMEOUT_MS))).catch(() => undefined);

        if (!result?.data) {
            return false;
        }

        const session = this.applySession(result.data);
        return session.isLoggedIn;
    }

    public async login(username: string, password: string): Promise<LoginResponse> {
        const result = await firstValueFrom(this.authApiService.login(username, password));
        this.applySession(result.data);
        return result;
    }

    public async twoFactor(name: string, code: string): Promise<LoginResponse> {
        const result = await firstValueFrom(this.authApiService.twoFactorLogin(name, code));

        if (result.message === 'Login successfull!') {
            this.applySession(result.data);
        }

        return result;
    }

    expired() {
        this._sessionData.next(new SessionData());
    }

    logout(): Observable<boolean> {
        return this.authApiService.logout().pipe(
            catchError((error) => {
                console.error('Faled to call logout endpoint:', error);
                return of(null);
            }),
            map(() => {
                this._sessionData.next(new SessionData());
                return true;
            })
        );
    }

    oidcLogout(): void {
        this._sessionData.next(new SessionData());

        window.location.href = this.authApiService.apiUrl('v1/auth/oidc/logout');
    }

    checkAndRenewToken(force: boolean = false, callback = (session?: SessionData) => {}) {
        this._sessionData.pipe(take(1)).subscribe((data) => {
            if (data && data.user.expires !== 0) {
                // If session expires in less than 30 minutes, renew the token
                const now = new Date();
                const expires = new Date(data.user.expires);
                const diff = expires.getTime() - now.getTime();
                const diffInMinutes = diff / 60000;
                if (force || diffInMinutes < 30) {
                    console.log(`The current session will expire in ${diffInMinutes} minutes. Renewing session...`);
                    this.authApiService.renewToken().subscribe({
                        next: (result) => {
                            if (result.message === 'Login successfull!') {
                                callback(this.applySession(result.data));
                            } else {
                                this.expired();
                                this.router.navigate(['/login']);
                            }
                        },
                        error: (error) => {
                            console.error(error);
                            this.expired();
                            this.router.navigate(['/login']);
                        }
                    });
                }
            }
        });
    }

    async impersonateUser(userId: string): Promise<boolean> {
        const result = await firstValueFrom(this.authApiService.createActorToken(userId));

        if (result && result.message === 'Impersonation start!') {
            this.applySession(result.data, 'pending');
            return true;
        }

        return false;
    }

    async endImpersonation(): Promise<boolean> {
        const result = await firstValueFrom(this.authApiService.revokeActorToken());

        if (result && result.message === 'Impersonation end!') {
            this.applySession(result.data, 'pending');
            return true;
        }

        return false;
    }
}
